import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { checkSubscriptionStatus, getSafeStripeReturnPath } from "../../services/stripeService";

function PremiumSuccess() {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const returnToPath = getSafeStripeReturnPath(searchParams.get('returnTo'));

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    console.log('[PremiumSuccess] Session ID:', sessionId);

    let cancelled = false;

    const verify = async (attempt = 1) => {
      try {
        const data = await checkSubscriptionStatus();
        if (cancelled) return;

        if (data.isPremium) {
          setVerified(true);
          setIsVerifying(false);
          // Notify App.js to re-fetch premium status
          window.dispatchEvent(new Event('authTokenChanged'));
          return;
        }
      } catch (err) {
        console.warn(`[PremiumSuccess] Verification attempt ${attempt} failed:`, err.message);
      }

      // Retry up to 3 times with 2s intervals (webhook may need time)
      if (!cancelled && attempt < 3) {
        setTimeout(() => verify(attempt + 1), 2000);
      } else if (!cancelled) {
        // After 3 attempts, show success anyway — webhook will catch up
        setVerified(true);
        setIsVerifying(false);
        window.dispatchEvent(new Event('authTokenChanged'));
      }
    };

    // Start first verification after 1.5s to give webhook time
    const timer = setTimeout(() => verify(1), 1500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchParams]);

  return (
    <main className="min-h-screen bg-[#fff] pt-[60px]">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center">
          {isVerifying ? (
            <>
              <div className="w-16 h-16 border-4 border-[#090] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h1 className="text-[#0d0c07] text-[32px] font-bold leading-[48px] mb-4">
                {t('premium.verifyingPayment')}
              </h1>
            </>
          ) : (
            <>
              {/* Success Icon */}
              <div className="w-20 h-20 bg-[#090] rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="#FFF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              
              <h1 className="text-[#0d0c07] text-[32px] font-bold leading-[48px] mb-4">
                {t('premium.successTitle')}
              </h1>
              <p className="text-[#56554D] text-[18px] leading-[28px] max-w-2xl mx-auto mb-8">
                {t('premium.successMessage')}
              </p>
              
              <Link
                to={returnToPath}
                className="
                  inline-flex justify-center items-center gap-2
                  h-[48px] px-[32px] py-[14px]
                  rounded-[32px]
                  bg-[#0D0C07]
                  text-[#FFF] text-center
                  text-[14.2px] font-semibold leading-[28px]
                  transition-colors
                  md:hover:bg-[#252218]
                "
              >
                {t('premium.startCreating')}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                    fill="currentColor"
                  />
                </svg>
              </Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default PremiumSuccess;
